/**
 * eventListenerManager.ts
 *
 * Centralized listener registration/cleanup for `VideoPlayer`.
 * - Purpose: attach/detach fw:* handlers to a specific host element and
 *   track attached maps in a WeakMap to allow safe cleanup when an
 *   element is removed.
 * - Exports: `registerVideoPlayerListeners(target, handlers, opts)` and
 *   `cleanupVideoPlayerListeners(target)`.
 * - Consumers: `apps/bfl-live-commerce-app/libs/src/components/VideoPlayer/VideoPlayer.tsx`
 */

export type VideoPlayerHandlers = {
  handleVideoStart: EventListener;
  handleProductClick: EventListener;
  handleVideoImpression: EventListener;
  handlePlayerQuit: EventListener;
};

type AttachedEntry = {
  targets: EventTarget[];
  map: Record<string, EventListener>;
};

const ATTACHED = new WeakMap<HTMLElement, AttachedEntry>();

export function registerVideoPlayerListeners(
  target: HTMLElement,
  handlers: VideoPlayerHandlers,
  options?: { enableTracking?: boolean }
): void {
  if (ATTACHED.has(target)) return;

  const eventTargets: EventTarget[] = [target];
  if (target.shadowRoot) {
    eventTargets.push(target.shadowRoot);
  }

  const map: Record<string, EventListener> = {};

  const always = [
    ["fw:player:quit", handlers.handlePlayerQuit],
    ["fw:video:start", handlers.handleVideoStart],
    ["fw:video:impression", handlers.handleVideoImpression],
  ] as const;

  for (const [evt, h] of always) {
    const fn = h as EventListener;
    for (const eventTarget of eventTargets) {
      eventTarget.addEventListener(evt, fn);
    }
    map[evt] = fn;
  }

  if (options?.enableTracking) {
    const tracked = [["fw:shopping:product-clicked", handlers.handleProductClick]] as const;

    for (const [evt, h] of tracked) {
      const fn = h as EventListener;
      for (const eventTarget of eventTargets) {
        eventTarget.addEventListener(evt, fn);
      }
      map[evt] = fn;
    }
  }

  ATTACHED.set(target, { targets: eventTargets, map });
}

export function cleanupVideoPlayerListeners(target: HTMLElement): void {
  const attached = ATTACHED.get(target);
  if (!attached) return;
  const { targets, map } = attached;
  for (const [evt, handler] of Object.entries(map)) {
    for (const eventTarget of targets) {
      try {
        eventTarget.removeEventListener(evt, handler);
      } catch {
        // ignore
      }
    }
  }
  ATTACHED.delete(target);
}

export default {
  registerVideoPlayerListeners,
  cleanupVideoPlayerListeners,
};
